import TabMenu from "components/project/TabMenu";
import GoBackButton from "components/project/GoBackButton";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppStore } from "lib/store/useAppStore";
import html2canvas from "html2canvas";

import { getFile } from "lib/api/getFile";
import { postSaveFrameFftInfo } from "lib/api/postSaveFrameFftInfo";

import VideoAnalysisPanel from "./analysis/VideoAnalysisPanel";
import Spinner from "components/Spinner";
import Ud1000ReportTypeVideo from "./report/Ud1000ReportTypeVideo";
import EditorBtnBox from "components/project/EditorBtnBox";
import VideoBackgroundCapturePortal from "components/project/VideoBackgroundCapturePotal";
import VideoThreeDPanel from "./spatial/VideoThreeDPanel";
import DownloadUdFile from "components/project/DownloadUdFile";
import PrevNextNavToFIleIndex from "components/project/PrevNextNavToFIleIndex";
import FileTreePath from "components/project/FileTreePath";
import { getFileSortPref } from "lib/utils/fileSortpref";
import { parseFileDetailFiltersFromSearchParams } from "lib/utils/fileDetailFilters";
import { resolveFileDetailTab } from "lib/utils/fileDetailTabs";
import {
  buildEditorSavePayload,
  isEditorFormChanged,
  saveEditorDraft,
} from "components/project/editorDraftUtils";

import HelpTip from "components/HelpTip";

const TAB_KEYS = {
  VIDEO_ANALYSIS: "videoAnalysis",
  REPORT: "report",
  REPORT_GENERATING: "reportGenerating",
  THREE_D: "threeD",
};

const TIP_KEY_BY_TAB = {
  [TAB_KEYS.VIDEO_ANALYSIS]: "VIDEO_FILE_VIDEO_ANALYSIS",
  [TAB_KEYS.REPORT]: "VIDEO_FILE_VIDEO_REPORT",
};

export default function VideoFilePage() {
  const { t, i18n } = useTranslation();
  const user = useAppStore((s) => s.user || null);
  const showAlert = useAppStore((s) => s.showAlert);
  const { project_id, upload_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailFilterKey = JSON.stringify(parseFileDetailFiltersFromSearchParams(searchParams));
  const detailFilters = useMemo(
    () => JSON.parse(detailFilterKey),
    [detailFilterKey],
  );
  const [file, setFile] = useState(null);

  const [prevData, setPrevData] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState([null, null]);
  const currentTab = resolveFileDetailTab(searchParams.get("tab"), "video");
  const setCurrentTab = (tab) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("tab", resolveFileDetailTab(tab, "video"));
      return next;
    }, { replace: true });
  };
  const [capturePotalStatus, setCapturePotalStatus] = useState(false);

  const [prevFile, setPrevFile] = useState(null);
  const [nextFile, setNextFile] = useState(null);

  console.log(selectedFrame);

  const TAB_LABELS = capturePotalStatus
    ? [
        {
          value: TAB_KEYS.VIDEO_ANALYSIS,
          label: t("videoFileIndex.tabs.videoAnalysis"),
        },
        {
          value: TAB_KEYS.REPORT_GENERATING,
          label: t("videoFileIndex.tabs.reportGenerating"),
        },
        { value: TAB_KEYS.THREE_D, label: t("videoFileIndex.tabs.threeD") },
      ]
    : [
        {
          value: TAB_KEYS.VIDEO_ANALYSIS,
          label: t("videoFileIndex.tabs.videoAnalysis"),
        },
        { value: TAB_KEYS.REPORT, label: t("videoFileIndex.tabs.report") },
        { value: TAB_KEYS.THREE_D, label: t("videoFileIndex.tabs.threeD") },
      ];

  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isReportSaving, setIsReportSaving] = useState(false);

  const refs = {
    frame_ref_1: useRef(null),
    graph_ref_1: useRef(null),
    spectrogram_ref_1: useRef(null),
    waveform_ref_1: useRef(null),
    frame_ref_2: useRef(null),
    graph_ref_2: useRef(null),
    spectrogram_ref_2: useRef(null),
    waveform_ref_2: useRef(null),
  };

  const fetchGetFile = useCallback(async () => {
    if (!project_id || !upload_id) return;

    try {
      const { sortKey, sortDir } = getFileSortPref(user?.id);
      const locale = i18n.resolvedLanguage || i18n.language || "ko-KR";

      const {
        success,
        message,
        messageKey,
        messageParams,
        data,
        prevFile,
        nextFile,
      } = await getFile(
        project_id,
        upload_id,
        user?.id,
        sortKey,
        sortDir,
        locale,
        detailFilters,
      );
      if (success) {
        setFile(data);
        setPrevData(data);
        setPrevFile(prevFile);
        setNextFile(nextFile);
        // if (data?.diagnosis_report_status === "not_started") {
        //   setCapturePotalStatus(true);
        // }
      } else {
        showAlert({
          text: message || "Failed to load file.",
          messageKey,
          messageParams,
          success: false,
          autoClose: true,
        });
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      showAlert({
        text: "An error occurred while loading file.",
        success: false,
        autoClose: true,
      });
      navigate(-1);
    }
  }, [
    project_id,
    upload_id,
    user?.id,
    navigate,
    showAlert,
    i18n.language,
    i18n.resolvedLanguage,
    detailFilters,
  ]);

  useEffect(() => {
    let alive = true;

    (async () => {
      await fetchGetFile();
      if (!alive) return;
    })();

    return () => {
      alive = false;
    };
  }, [fetchGetFile]);

  useEffect(() => {
    setSelectedFrame([null, null]);
  }, [currentTab]);

  const captureRefImage = async (ref) => {
    if (!ref?.current) return null;

    await new Promise((r) => setTimeout(r, 350));

    const canvas = await html2canvas(ref.current, {
      scale: 1,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      logging: false,
    });

    return canvas.toDataURL("image/png");
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    if (selectedFrame.some((v) => v == null)) {
      showAlert({
        text: "Please select both frames.",
        success: false,
        autoClose: true,
      });
      return;
    }

    setIsLoading(true);

    const merged = [
      {
        ...selectedFrame[0],
        graph: await captureRefImage(refs.graph_ref_1),
        image: await captureRefImage(refs.frame_ref_1),
        waveform: await captureRefImage(refs.waveform_ref_1),
      },
      {
        ...selectedFrame[1],
        graph: await captureRefImage(refs.graph_ref_2),
        image: await captureRefImage(refs.frame_ref_2),
        waveform: await captureRefImage(refs.waveform_ref_2),
      },
    ];

    const hasInvalid = merged.some((f) => !f.graph || !f.image || !f.waveform);

    if (hasInvalid) {
      showAlert({
        text: "Some capture images are missing. Please retry.",
        success: false,
        autoClose: true,
      });
      setIsLoading(false);
      return;
    }

    const times = [selectedFrame[0].time, selectedFrame[1].time];

    const props = {
      upload_id,
      clean_file_name: file?.clean_file_name,
      times,
      frames: merged,
      volume: file?.volume,
      locale: i18n?.language || "ko-KR",
    };

    const { success, message, messageKey, messageParams } =
      await postSaveFrameFftInfo(props);

    if (success) {
      await fetchGetFile();
    }

    showAlert({
      success,
      text: message,
      messageKey,
      messageParams,
    });

    setIsLoading(false);
  };

  const ReportProps = {
    form: file,
    setForm: setFile,
    prevData,
    setPrevData,
    isReadOnly,
    setIsReadOnly,
    type: "file",
    user_id: user?.id,
    project_id,
    refetch: fetchGetFile,
  };

  const hasReportDraftChanges = isEditorFormChanged(
    ReportProps.form,
    ReportProps.prevData,
  );

  const handleSaveReport = async () => {
    if (!ReportProps.prevData || isReportSaving) return;

    setIsReportSaving(true);
    await saveEditorDraft(buildEditorSavePayload(ReportProps, t));
    setIsReportSaving(false);
  };

  if (!file) {
    return (
      <section className="content">
        <Spinner />
      </section>
    );
  }

  return (
    <section className="fileDetail content">
      <div className="fileDetailHeader">
        <div className="fileDetailHeader__main">
          <GoBackButton t={t} index="temp" />
          <div className="fileDetailHeader__identity">
            <FileTreePath file={file} uploadId={upload_id} />
            <div className="editorFormTitleBox helpTitleRow">
              <h2 title={file?.file_name} tabIndex={0}>{file?.file_name ?? "불러오는 중..."}</h2>
            </div>
          </div>
          <PrevNextNavToFIleIndex
            project_id={project_id}
            upload_id={upload_id}
            prevFile={prevFile}
            nextFile={nextFile}
            currentTab={currentTab}
          />
        </div>

        <div className="fileDetail__toolbar">
          <div className="fileDetailHeader__tabs">
            <TabMenu
              labels={TAB_LABELS}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              disabledValues={[TAB_KEYS.REPORT_GENERATING]}
            />
            {TIP_KEY_BY_TAB[currentTab] && (
              <HelpTip
                tipKey={TIP_KEY_BY_TAB[currentTab]}
                className="fileDetailHeader__help"
                placement="bottom"
                maxWidth={360}
              />
            )}
          </div>
          <div className="fileDetailHeader__actions">
            <DownloadUdFile t={t} streamUrl={file?.ud_path} />
            {/* <MultiImageDownloadBtn file={file} /> */}
            {currentTab === TAB_KEYS.VIDEO_ANALYSIS && (
              <button onClick={handleSubmit} className="btn-bg-blue fileDetailHeader__primaryAction">
                {t("videoFileIndex.buttons.frameFftSave")}
              </button>
            )}
          </div>
        </div>
      </div>

      {currentTab === TAB_KEYS.VIDEO_ANALYSIS && (
        <VideoAnalysisPanel
          file={file}
          refs={refs}
          selectedFrame={selectedFrame}
          setSelectedFrame={setSelectedFrame}
          t={t}
        />
      )}
      {currentTab === TAB_KEYS.REPORT && (
        <Ud1000ReportTypeVideo
          isReadOnly={isReadOnly}
          file={file}
          form={prevData}
          setForm={setPrevData}
          setIsReadOnly={setIsReadOnly}
          t={t}
          reportActionSlot={<EditorBtnBox props={ReportProps} />}
          onSave={handleSaveReport}
          isSaving={isReportSaving}
          hasChanges={hasReportDraftChanges}
        />
      )}

      {currentTab === TAB_KEYS.THREE_D && (
        <VideoThreeDPanel
          t={t}
          upload_status={file?.upload_status}
          upload_id={upload_id}
          fetchGetFile={fetchGetFile}
          plyUrl={file?.mesh_ply_path}
          textureUrl={file?.mesh_png_path}
          framesUrl={file?.colmap_frames_txt_path}
          imagesUrl={file?.colmap_images_txt_path}
          points3DUrl={file?.colmap_points3d_txt_path}
        />
      )}

      {capturePotalStatus && (
        <VideoBackgroundCapturePortal
          file={file}
          fetchGetFile={fetchGetFile}
          setCapturePotalStatus={setCapturePotalStatus}
          locale={i18n?.language}
        />
      )}

      {isLoading && <Spinner />}
    </section>
  );
}
